import { Component, OnInit, PLATFORM_ID, Inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { SupportService, IssueCategory } from '../../shared/services/support.service';

@Component({
  selector: 'app-support',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './support.component.html',
  styleUrl: './support.component.scss',
  host: { id: 'app-support-main' },
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('600ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ]),
    trigger('formAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(30px)' }),
        animate('800ms 300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ])
  ]
})
export class SupportComponent implements OnInit {
  supportForm: FormGroup;
  isSubmitting = false;
  submitted = false;
  submitError = false;
  submittedTicketId: string | null = null;
  isBrowser: boolean;

  categories: IssueCategory[] = [];
  categoriesLoading = true;
  categoriesError = false;
  currentLang: string;

  constructor(
    private fb: FormBuilder,
    private supportService: SupportService,
    private translateService: TranslateService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.currentLang = this.translateService.currentLang || 'en';

    this.supportForm = this.fb.group({
      fullName:         ['', [Validators.required, Validators.maxLength(100)]],
      mobile:           ['', [Validators.required, Validators.pattern(/^\+968[0-9]{8}$/)]],
      email:            ['', [Validators.required, Validators.email, Validators.maxLength(200)]],
      branchName:       ['', [Validators.required, Validators.maxLength(100)]],
      zIssueCategoryId: ['', [Validators.required]],
      subject:          ['', [Validators.required, Validators.maxLength(200)]],
      issueDesc:        ['', [Validators.required, Validators.maxLength(2000)]]
    });
  }

  ngOnInit(): void {
    this.loadCategories();

    this.translateService.onLangChange.subscribe(event => {
      this.currentLang = event.lang;
    });
  }

  loadCategories(): void {
    this.supportService.getIssueCategories().subscribe({
      next: (data) => {
        this.categories = data;
        this.categoriesLoading = false;
      },
      error: () => {
        this.categoriesError = true;
        this.categoriesLoading = false;
      }
    });
  }

  getCategoryName(category: IssueCategory): string {
    return this.currentLang === 'ar' ? category.nameAr : category.nameEn;
  }

  onSubmit(): void {
    if (!this.isBrowser) return;

    if (this.supportForm.invalid) {
      Object.keys(this.supportForm.controls).forEach(key => {
        this.supportForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.isSubmitting = true;
    this.submitted = false;
    this.submitError = false;

    const payload = {
      ...this.supportForm.value,
      zIssueCategoryId: Number(this.supportForm.value.zIssueCategoryId)
    };

    this.supportService.submitSupportTicket(payload).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        this.submitted = true;
        this.submittedTicketId = response.supportId;
        this.supportForm.reset();
      },
      error: () => {
        this.isSubmitting = false;
        this.submitError = true;
      }
    });
  }

  get issueDescLength(): number {
    return this.supportForm.get('issueDesc')?.value?.length ?? 0;
  }
}
